import { http } from "../helpers/easyindustriaapi/config";

export default class PedidoManifestoController {
  /**
   * Busca grupos por empresa
   * @param {number} empresaId - ID da empresa
   * @returns {Promise<Array>} - Lista de grupos
   */
  static async getGrupos(empresaId) {
    const response = await http.get(`grupo?empresa_id=${empresaId}`);
    return response.data;
  }

  /**
   * Envia o arquivo XML para processamento no back-end
   * @param {FormData} formData - FormData contendo o arquivo e metadados
   * @returns {Promise<Object>} - Resultado do processamento realizado no servidor
   */
  static async importarNota(formData) {
    formData.append("empresa_id", formData.get("empresa_id") || "");
    const response = await http.post("nfe/arquivo", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  }

  /**
   * Busca relacionamento produto-fornecedor
   * @param {number} empresaId - ID da empresa
   * @param {string} codExterno - Código do produto no fornecedor
   * @param {string} descricaoFornecedor - Descrição do produto no fornecedor
   * @returns {Promise<Object>} - Relacionamento encontrado
   */
  static async buscarRelacionamento(empresaId, codExterno, descricaoFornecedor, pessoaId = null) {
    const params = {
      empresa_id: empresaId,
      cod_externo: String(codExterno).trim(),
      fornecedor_descricao: String(descricaoFornecedor).trim(),
    };

    // Adiciona pessoa_id quando disponível para busca mais específica
    if (pessoaId) {
      params.pessoa_id = pessoaId;
    }

    const response = await http.get('int-produto-fornecedor/cod-externo', { params });
    return response.data;
  }

  /**
   * Cria produtos em lote
   * @param {Array} produtos - Array de produtos para criar
   * @returns {Promise<Object>} - Resultado da criação em lote
   */
  static async createProdutosBatch(produtos) {
    const created = [];
    const errors = [];

    for (const produto of produtos) {
      try {
        const response = await http.post('produto', produto);
        created.push(response.data);
      } catch (error) {
        errors.push({
          produto: produto,
          error: error.response?.data?.message || error.message
        });
      }
    }

    return {
      created,
      errors
    };
  }

  /**
   * Cria relacionamentos produto-fornecedor em lote
   * @param {number} empresaId - ID da empresa
   * @param {Array} relacionamentos - Array de relacionamentos para criar
   * @returns {Promise<Object>} - Resultado da criação em lote
   */
  static async createRelacionamentosBatch(empresaId, relacionamentos) {
    const created = [];
    const errors = [];

    for (const relacionamento of relacionamentos) {
      try {
        const response = await http.post(
          "int-produto-fornecedor/processar-com-transacao",
          relacionamento
        );
        created.push(response.data);
      } catch (error) {
        errors.push({
          relacionamento: relacionamento,
          error: error.response?.data?.message || error.message
        });
      }
    }

    return {
      created,
      errors
    };
  }

  /**
   * Busca pessoa por CPF/CNPJ
   * @param {string} cpfCnpj - CPF ou CNPJ da pessoa
   * @param {number} empresaId - ID da empresa
   * @returns {Promise<Object>} - Dados da pessoa encontrada
   */
  static async buscarPessoaPorCpfCnpj(cpfCnpj, empresaId) {
    const response = await http.get(`pessoa/buscarPorCnpj?cnpj=${cpfCnpj}&empresa_id=${empresaId}`);
    return response.data;
  }

  /**
   * Cria uma nova pessoa (fornecedor)
   * @param {Object} pessoaData - Dados da pessoa
   * @returns {Promise<Object>} - Dados da pessoa criada
   */
  static async createPessoa(pessoaData) {
    const response = await http.post('pessoa', pessoaData);
    return response.data;
  }

  /**
   * Processa arquivo XML e extrai produtos
   * @param {File} file - Arquivo XML
   * @param {Object} empresaData - Dados da empresa logada
   * @param {Object} configuracaoEmpresa - Configuração da empresa
   * @returns {Promise<Array>} - Lista de produtos extraídos
   */
  static async processarXML(file, empresaData, configuracaoEmpresa) {
    try {
      // Lê o conteúdo do arquivo
      const conteudo = await this.readFileContent(file);
      
      // Parse do XML
      const xmlDocument = new DOMParser().parseFromString(conteudo, "text/xml");
      
      // Verifica se houve erro de parsing
      if (xmlDocument && xmlDocument.querySelector && xmlDocument.querySelector("parsererror")) {
        throw new Error("XML inválido");
      }

      // Helper para ler texto de elementos com segurança
      const getText = (parent, selector, fallback = "") => {
        if (!parent) return fallback;
        const el = selector ? parent.querySelector(selector) : parent;
        return el && el.textContent != null ? el.textContent : fallback;
      };

      // Extrai informações do emitente (fornecedor)
      const emitElement = xmlDocument.querySelector("emit");
      if (!emitElement) {
        throw new Error("Tag emit não encontrada no XML");
      }

      const nomeFornecedor = getText(emitElement, "xNome", "");
      const cnpjFornecedor = getText(emitElement, "CNPJ", "");

      // Extrai produtos do XML
      const produtos = xmlDocument.querySelectorAll("det");
      const produtosExtraidos = [];

      for (let i = 0; i < produtos.length; i++) {
        const produto = produtos[i];
        const codFornec = getText(produto, "prod > cProd", "");
        const nomeProduto = getText(produto, "prod > xProd", "");
        const unidadeMedida = getText(produto, "prod > uCom", "");
        
        if (codFornec && nomeProduto) {
          const dadosProduto = await this.processarProdutoXML(
            produto, 
            codFornec, 
            nomeProduto, 
            unidadeMedida,
            nomeFornecedor, 
            cnpjFornecedor, 
            empresaData, 
            configuracaoEmpresa
          );
          
          if (dadosProduto) {
            produtosExtraidos.push(dadosProduto);
          }
        }
      }

      return {
        produtos: produtosExtraidos,
        fornecedor: {
          cnpj: cnpjFornecedor,
          nome: nomeFornecedor,
          fantasia: getText(emitElement, "xFant", ""),
          ie: getText(emitElement, "IE", ""),
          endereco: {
            logradouro: getText(emitElement, "enderEmit > xLgr", ""),
            numero: getText(emitElement, "enderEmit > nro", ""),
            bairro: getText(emitElement, "enderEmit > xBairro", ""),
            cidade_nome: getText(emitElement, "enderEmit > xMun", ""),
            uf: getText(emitElement, "enderEmit > UF", ""),
            cep: getText(emitElement, "enderEmit > CEP", "")
          }
        }
      };
    } catch (error) {
      throw new Error(`Erro ao processar XML: ${error.message}`);
    }
  }

  /**
   * Processa um produto individual do XML
   * @param {Element} produtoElement - Elemento do produto no XML
   * @param {string} codFornec - Código do fornecedor
   * @param {string} nomeProduto - Nome do produto
   * @param {string} unidadeMedida - Unidade de medida
   * @param {string} nomeFornecedor - Nome do fornecedor
   * @param {string} cnpjFornecedor - CNPJ do fornecedor
   * @param {Object} empresaData - Dados da empresa
   * @param {Object} configuracaoEmpresa - Configuração da empresa
   * @returns {Promise<Object>} - Dados do produto processado
   */
  static async processarProdutoXML(produtoElement, codFornec, nomeProduto, unidadeMedida, nomeFornecedor, cnpjFornecedor, empresaData, configuracaoEmpresa) {
    const getText = (parent, selector, fallback = "") => {
      if (!parent) return fallback;
      const el = selector ? parent.querySelector(selector) : parent;
      return el && el.textContent != null ? el.textContent : fallback;
    };

    // Estrutura os dados básicos do produto
    const dadosProduto = {
      cod_fornec: codFornec,
      nome_fornecedor: nomeFornecedor,
      cnpj_fornecedor: cnpjFornecedor,
      unidade_fornecedor: unidadeMedida,
    };

    // PRIMEIRO: Aplica grupo_id da configuração para todos os produtos
    console.log(`Aplicando grupo_id da configuração para produto ${codFornec}`);
    if (configuracaoEmpresa && configuracaoEmpresa.grupo_id) {
      dadosProduto.grupo_id = configuracaoEmpresa.grupo_id;
      console.log(`Grupo aplicado da configuração: ${configuracaoEmpresa.grupo_id}`);
    } else {
      console.warn('Configuração da empresa não possui grupo_id');
      dadosProduto.grupo_id = null;
    }

    // SEGUNDO: Verifica se já existe relacionamento na tabela int_produto_fornecedor
    let relacionamentoExistente = null;
    let produtoEmpresa = null;
    
    try {
      const relacionamento = await this.buscarRelacionamento(empresaData.id, codFornec, nomeProduto);
      
      if (relacionamento && relacionamento.produto) {
        relacionamentoExistente = relacionamento;
        produtoEmpresa = relacionamento.produto;
        console.log(`Produto existente encontrado: ${produtoEmpresa.produto_nome}`);
        
        // TERCEIRO: Se produto existe, carrega TODOS os dados do produto existente
        dadosProduto.cod_empresa = produtoEmpresa?.produto_cod_int || produtoEmpresa?.codbarras || produtoEmpresa?.produto_cod || "";
        dadosProduto.nome_empresa = produtoEmpresa?.produto_nome || "";
        dadosProduto.fator_conv = relacionamentoExistente.fornecedor_fator || "1.00";
        dadosProduto.relacionamento_id = relacionamentoExistente.id;
        dadosProduto.produto_id = relacionamentoExistente.produto_id;
        dadosProduto.status = "existente";
        dadosProduto.produto_dados = produtoEmpresa;
        
        // Se o produto existente tem grupo_id, sobrescreve o da configuração
        if (produtoEmpresa.grupo_id) {
          dadosProduto.grupo_id = produtoEmpresa.grupo_id;
          console.log(`Grupo sobrescrito com o do produto existente: ${produtoEmpresa.grupo_id}`);
        }
      }
    } catch (error) {
      // Se o erro for 404 (não encontrado), é esperado para produtos novos
      if (error.response && error.response.status !== 404) {
        console.error(`Erro ao buscar produto ${codFornec}:`, error.message);
      }
    }

    // Se não encontrou relacionamento, define como produto novo
    if (!relacionamentoExistente) {
      dadosProduto.cod_empresa = "";
      dadosProduto.nome_empresa = "";
      dadosProduto.fator_conv = "1.00";
      dadosProduto.relacionamento_id = null;
      dadosProduto.produto_id = null;
      dadosProduto.status = "novo";
      dadosProduto.produto_dados = null;
      console.log(`Produto novo: ${codFornec} - mantém grupo da configuração: ${dadosProduto.grupo_id}`);
    }

    // Adiciona dados XML ao objeto
    dadosProduto.dados_xml = {
      xProd: nomeProduto,
      cProd: codFornec,
      qCom: getText(produtoElement, "prod > qCom", ""),
      vUnCom: getText(produtoElement, "prod > vUnCom", ""),
      vProd: getText(produtoElement, "prod > vProd", ""),
      uCom: unidadeMedida,
      ncm: getText(produtoElement, "prod > NCM", ""),
      cfop: getText(produtoElement, "prod > CFOP", ""),
      cest: getText(produtoElement, "prod > CEST", ""),
      indTot: getText(produtoElement, "prod > indTot", ""),
      // Dados de impostos
      icms: this.extrairDadosImpostos(produtoElement)
    };

    return dadosProduto;
  }

  /**
   * Extrai dados de impostos do XML
   * @param {Element} produtoElement - Elemento do produto no XML
   * @returns {Object} - Dados de impostos extraídos
   */
  static extrairDadosImpostos(produtoElement) {
    const getText = (parent, selector, fallback = "") => {
      if (!parent) return fallback;
      const el = selector ? parent.querySelector(selector) : parent;
      return el && el.textContent != null ? el.textContent : fallback;
    };

    const impostoElement = produtoElement.querySelector("imposto");
    if (!impostoElement) {
      return {
        picms: "0",
        cst: "00",
        cstpis: "00",
        cstcofins: "00",
        orig: "0",
        cest: "0",
        class_sped: "00",
        cstipi: "00",
        pbc_pis: "100",
        pbc_cofins: "100",
        pbc_icms: "100",
        pbc_ipi: "100",
        pcofins: "100",
        ppis: "100",
        pipi: "100"
      };
    }

    // Extrai dados do ICMS
    let picms = "0";
    let cst = "00";
    let orig = "0";
    let pbc_icms = "100";

    const icmsElement = impostoElement.querySelector("ICMS");
    if (icmsElement) {
      const icmsGroups = [
        "ICMS00", "ICMS10", "ICMS20", "ICMS30", "ICMS40", "ICMS51", "ICMS60", "ICMS70", "ICMS90",
        "ICMSSN101", "ICMSSN102", "ICMSSN201", "ICMSSN202", "ICMSSN500", "ICMSSN900"
      ];

      for (const group of icmsGroups) {
        const groupElement = icmsElement.querySelector(group);
        if (groupElement) {
          if (group === "ICMSSN102") {
            cst = getText(groupElement, "CSOSN", "00");
            picms = "0";
            pbc_icms = getText(groupElement, "pBCICMS", "100");
          } else {
            cst = getText(groupElement, "CST", "00");
            picms = getText(groupElement, "pICMS", "0");
            pbc_icms = getText(groupElement, "pBCICMS", "100");
          }
          orig = getText(groupElement, "orig", "0");
          break;
        }
      }
    }

    // Extrai dados do PIS
    let cstpis = "00";
    let pbc_pis = "100";
    let ppis = "100";
    const pisElement = impostoElement.querySelector("PIS");
    if (pisElement) {
      cstpis = getText(pisElement, "CST", "00");
      const pisAliqElement = pisElement.querySelector("PISAliq");
      if (pisAliqElement) {
        pbc_pis = getText(pisAliqElement, "pBcCOFINS", "100");
        ppis = getText(pisAliqElement, "pPIS", "100");
      }
    }

    // Extrai dados do COFINS
    let cstcofins = "00";
    let pbc_cofins = "100";
    let pcofins = "100";
    const cofinsElement = impostoElement.querySelector("COFINS");
    if (cofinsElement) {
      cstcofins = getText(cofinsElement, "CST", "00");
      const cofinsAliqElement = cofinsElement.querySelector("COFINSAliq");
      if (cofinsAliqElement) {
        pbc_cofins = getText(cofinsAliqElement, "pBcCOFINS", "100");
        pcofins = getText(cofinsAliqElement, "pCOFINS", "100");
      }
    }

    // Extrai dados do IPI
    let cstipi = "00";
    let pbc_ipi = "100";
    let pipi = "100";
    const ipiElement = impostoElement.querySelector("IPI");
    if (ipiElement) {
      const ipintElement = ipiElement.querySelector("IPINT");
      if (ipintElement) {
        cstipi = getText(ipintElement, "CST", "00");
      }
      
      const ipiTribElement = ipiElement.querySelector("IPITrib");
      if (ipiTribElement) {
        pbc_ipi = getText(ipiTribElement, "pbBcIPI", "100");
        pipi = getText(ipiTribElement, "pIPI", "100");
      }
    }

    return {
      picms: picms,
      cst: cst,
      cstpis: cstpis,
      cstcofins: cstcofins,
      orig: orig,
      cest: "0",
      class_sped: "00",
      cstipi: cstipi,
      pbc_pis: pbc_pis,
      pbc_cofins: pbc_cofins,
      pbc_icms: pbc_icms,
      pbc_ipi: pbc_ipi,
      pcofins: pcofins,
      ppis: ppis,
      pipi: pipi
    };
  }

  /**
   * Lê o conteúdo de um arquivo
   * @param {File} file - Arquivo para ler
   * @returns {Promise<string>} - Conteúdo do arquivo
   */
  static readFileContent(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        resolve(e.target.result);
      };

      reader.onerror = (e) => {
        reject(e.target.error);
      };

      reader.readAsText(file);
    });
  }

  /**
   * Valida estrutura do XML
   * @param {File} file - Arquivo XML
   * @returns {boolean} - Se o XML é válido
   */
  static async validarXML(file) {
    try {
      if (file.type !== "text/xml" && !file.name.endsWith('.xml')) {
        return false;
      }
      
      const conteudo = await this.readFileContent(file);
      const xmlDocument = new DOMParser().parseFromString(conteudo, "text/xml");
      
      return !(xmlDocument && xmlDocument.querySelector && xmlDocument.querySelector("parsererror"));
    } catch (error) {
      return false;
    }
  }

  /**
   * Busca todas as tabelas de CST de impostos com cache otimizado
   * Evita múltiplas requisições e implementa cache local
   * @returns {Promise<Object>} - Tabelas de CST (ICMS, PIS, COFINS, IPI)
   */
  static async getTabelasImpostos() {
    const cacheKey = 'tabelas_impostos_cst';
    const cacheExpiry = 24 * 60 * 60 * 1000; // 24 horas em ms
    
    try {
      // Verifica cache local primeiro
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const parsedData = JSON.parse(cachedData);
          const now = Date.now();
          
          // Se ainda não expirou, retorna do cache
          if (parsedData.timestamp && (now - parsedData.timestamp) < cacheExpiry) {
            console.log('Retornando tabelas de impostos do cache local');
            console.log('Dados do cache:', parsedData.data);
            return parsedData.data;
          } else {
            console.log('Cache expirado, recarregando da API...');
          }
        } catch (parseError) {
          console.warn('Erro ao fazer parse do cache, limpando e recarregando:', parseError);
          localStorage.removeItem(cacheKey);
        }
      } else {
        console.log('Cache não encontrado, carregando da API...');
      }

      // Se não tem cache ou expirou, busca da API
      console.log('Fazendo requisição para: tabelas/impostos-cst');
      const response = await http.get('tabelas/impostos-cst');
      
      console.log('Resposta da API recebida:', response);
      console.log('response.data:', response.data);
      
      // Verifica se a resposta tem dados válidos
      if (response && response.data) {
        let tabelas = null;
        
        // Tenta diferentes formatos de resposta
        if (response.data.success && response.data.data) {
          tabelas = response.data.data;
        } else if (response.data.icms || response.data.pis || response.data.cofins || response.data.ipi) {
          // Resposta direta sem wrapper
          tabelas = response.data;
        }
        
        if (tabelas) {
          // Salva no cache local com timestamp
          const cacheData = {
            timestamp: Date.now(),
            data: tabelas
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          
          console.log('Tabelas de impostos carregadas e salvas no cache:', {
            icms: tabelas.icms?.length || 0,
            pis: tabelas.pis?.length || 0,
            cofins: tabelas.cofins?.length || 0,
            ipi: tabelas.ipi?.length || 0
          });
          
          return tabelas;
        }
      }
      
      throw new Error('Resposta inválida da API de tabelas de impostos');
      
    } catch (error) {
      console.error('Erro ao carregar tabelas de impostos:', error);
      console.error('Detalhes do erro:', error.response || error.message);
      
      // Fallback: tenta usar cache expirado se disponível
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          console.warn('Usando cache expirado como fallback');
          const parsedData = JSON.parse(cachedData);
          return parsedData.data;
        } catch (parseError) {
          console.error('Erro ao fazer parse do cache expirado:', parseError);
        }
      }
      
      // Fallback final: retorna dados básicos
      console.warn('Usando dados básicos de fallback');
      return {
        icms: [{ cst: '00', descricao: 'Tributada integralmente' }],
        pis: [{ cst: '00', descricao: 'Operação tributável' }],
        cofins: [{ cst: '00', descricao: 'Operação tributável' }],
        ipi: [{ cst: '00', descricao: 'Entrada com recuperação de crédito' }]
      };
    }
  }
}
